// ==================== TOP CAMPAIGNS COMPONENT ====================

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trophy, TrendingUp, Eye, ArrowRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card';
import { cn } from '../../../utils/helpers';
import { staggerItem } from '../../../utils/animations';
import type { TopCampaignItem } from '../../../services/customer-dashboard.service';

// ==================== TYPES ====================

interface TopCampaignsProps {
  campaigns: TopCampaignItem[];
  isLoading?: boolean;
}

// ==================== COMPONENT ====================

export const TopCampaigns = ({ campaigns, isLoading = false }: TopCampaignsProps) => {
  if (isLoading) {
    return (
      <motion.div variants={staggerItem}>
        <Card>
          <CardHeader className="border-b-0 pb-1 mb-0">
            <CardTitle className="text-base">Top Performing Campaigns</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="space-y-2 animate-pulse">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-12 bg-(--color-border-gray) rounded" />
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <motion.div variants={staggerItem}>
        <Card>
          <CardHeader className="border-b-0 pb-1 mb-0">
            <CardTitle className="text-base">Top Performing Campaigns</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="py-4 text-center">
              <Trophy size={24} className="text-(--color-muted-gray) mx-auto mb-1" />
              <p className="text-xs text-(--color-muted-gray)">No completed campaigns yet</p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div variants={staggerItem}>
      <Card>
        <CardHeader className="border-b-0 pb-1 mb-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-1.5">
              <Trophy size={14} className="text-yellow-400" />
              Top Campaigns
            </CardTitle>
            <Link 
              to="/app/campaigns" 
              className="flex items-center gap-1 text-xs text-(--color-signal-orange) hover:underline"
            >
              View All
              <ArrowRight size={10} />
            </Link>
          </div>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="space-y-2">
            {campaigns.map((campaign, index) => (
              <Link
                key={campaign.id}
                to={`/app/campaigns/${campaign.id}`}
                className="flex items-center gap-2 p-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray) hover:border-(--color-signal-orange)/30 transition-colors"
              >
                {/* Rank */}
                <div className={cn(
                  'w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold',
                  index === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                  index === 1 ? 'bg-gray-400/20 text-gray-300' :
                  index === 2 ? 'bg-orange-600/20 text-orange-400' :
                  'bg-(--color-border-gray) text-(--color-muted-gray)'
                )}>
                  {index + 1}
                </div>

                {/* Campaign Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-white truncate">{campaign.name}</p>
                  <p className="text-[10px] text-(--color-muted-gray)">
                    {campaign.sentCount.toLocaleString()} sent
                  </p>
                </div>

                {/* Metrics */}
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 text-[10px]">
                    <TrendingUp size={10} className="text-green-400" />
                    <span className="text-green-400">{campaign.deliveryRate}%</span>
                  </div>
                  <div className="flex items-center gap-1 text-[10px]">
                    <Eye size={10} className="text-blue-400" />
                    <span className="text-blue-400">{campaign.readRate}%</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default TopCampaigns;
